import {
  BALI_DESTINATION,
  CALIFORNIA_COAST_DESTINATION,
  CARIBBEAN_DESTINATION,
  CHARLESTON_DESTINATION,
  CONTINENTAL_US_SUBDESTINATION,
  EUROPE_SUBDESTINATION,
  FLORIDA_KEYS_DESTINATION,
  FRANCE_DESTINATION,
  GREECE_DESTINATION,
  HAWAII_DESTINATION,
  ITALY_DESTINATION,
  MALDIVES_DESTINATION,
  MEXICO_CENTRAL_AMERICA_DESTINATION,
  NAPA_DESTINATION,
  OTHER_SUBDESTINATION,
  PARKS_DESTINATION,
  SPAIN_DESTINATION,
} from 'constants/honeymoons/honeymoonDestinations';
import { SecondaryNavIdentifier } from '@zola-helpers/client/dist/es/constants/navConstants';
import { WProductView } from '@zola/svc-web-api-ts-client';
import { CategoryTileProps } from '@zola/zola-ui/src/components/PreAuthV3/CategoryTile';
import P from '@zola/zola-ui/src/typography/Paragraphs';
import styled from '@emotion/styled';
import { FONT } from '@zola/zola-ui/src/styles/emotion';
import hotelData from '../../constants/honeymoons/honeymoons_hotels_raw.json';
import experienceData from '../../constants/honeymoons/honeymoons_experiences_raw.json';
import cruiseData from '../../constants/honeymoons/honeymoons_cruises.json';

import {
  HoneymoonCruise,
  HoneymoonDestinationSlug,
  HoneymoonExperience,
  HoneymoonExperienceName,
  HoneymoonHotel,
} from './types';
import { getFormattedPrice } from './productUtils';

export const getHotelsByDestination = (destinationNames: string[]): HoneymoonHotel[] => {
  const hotelsForDestination = hotelData.filter(hotel => destinationNames.includes(hotel.category));

  return hotelsForDestination.map(hotel => {
    return {
      category: hotel.category,
      location: hotel.location,
      displayLocation: hotel.display_location,
      displayName: hotel.display_name,
      partner: hotel.partner,
      webUrl: hotel.web_url,
      pricePerNight: hotel.price_per_night,
      displayPrice: hotel.display_price,
      allInclusive: hotel.all_inclusive_string === 'Y',
      displayUrl: hotel.display_url,
      imageAttribution: hotel.image_attribution,
      notes: hotel.notes,
    };
  });
};

export const getCruisesByDestination = (destinationNames: string[]): HoneymoonCruise[] => {
  const cruisesForDestination = cruiseData.filter(cruise =>
    destinationNames.includes(cruise.category)
  );

  return cruisesForDestination.map(cruise => {
    return {
      category: cruise.category,
      displayLocation: cruise.display_location,
      displayName: cruise.display_name,
      partner: cruise.partner,
      webUrl: cruise.web_url,
      displayUrl: cruise.display_url,
      imageAttribution: cruise.image_attribution,
    };
  });
};

export const getThirdPartyExperienceImage = (experience: HoneymoonExperience) => {
  switch (experience.displayName) {
    case 'Full day trips':
      return 'https://d1tntvpcrzvon2.cloudfront.net/static-assets/images/honeymoon-affiliates/experience_categories/full_day_v2.jpg';
    case 'Extreme sports':
      return 'https://d1tntvpcrzvon2.cloudfront.net/static-assets/images/honeymoon-affiliates/experience_categories/extreme_sports.jpg';
    case 'Cruises and sailing':
      return 'https://d1tntvpcrzvon2.cloudfront.net/static-assets/images/honeymoon-affiliates/experience_categories/cruises_sailing.jpg';
    case 'Food and drink':
      return 'https://d1tntvpcrzvon2.cloudfront.net/static-assets/images/honeymoon-affiliates/experience_categories/food_drink.jpg';
    case 'Nature and wildlife':
      return 'https://d1tntvpcrzvon2.cloudfront.net/static-assets/images/honeymoon-affiliates/experience_categories/nature_v2.jpg';
    default:
      return '';
  }
};
export const getExperiencesByDestination = (destinationNames: string[]): HoneymoonExperience[] => {
  const experiencesForDestination = experienceData.filter(experience =>
    destinationNames.includes(experience.category)
  );

  return experiencesForDestination
    .map(experience => {
      return {
        category: experience.category,
        partner: experience.partner,
        displayName: experience.display_name as HoneymoonExperienceName,
        webUrl: experience.web_url,
      };
    })
    .filter(experience => experience.displayName !== 'On Zola');
};

export const getHoneymoonDestinationBySlug = (slug: HoneymoonDestinationSlug) => {
  switch (slug) {
    case HoneymoonDestinationSlug.HAWAII:
      return HAWAII_DESTINATION;
    case HoneymoonDestinationSlug.CARIBBEAN:
      return CARIBBEAN_DESTINATION;
    case HoneymoonDestinationSlug.ITALY:
      return ITALY_DESTINATION;
    case HoneymoonDestinationSlug.GREECE:
      return GREECE_DESTINATION;
    case HoneymoonDestinationSlug.SPAIN:
      return SPAIN_DESTINATION;
    case HoneymoonDestinationSlug.FRANCE:
      return FRANCE_DESTINATION;
    case HoneymoonDestinationSlug.MEXICO_CENTRAL_AMERICA:
      return MEXICO_CENTRAL_AMERICA_DESTINATION;
    case HoneymoonDestinationSlug.FLORIDA_KEYS:
      return FLORIDA_KEYS_DESTINATION;
    case HoneymoonDestinationSlug.NAPA:
      return NAPA_DESTINATION;
    case HoneymoonDestinationSlug.CALIFORNIA_COAST:
      return CALIFORNIA_COAST_DESTINATION;
    case HoneymoonDestinationSlug.NATIONAL_PARKS:
      return PARKS_DESTINATION;
    case HoneymoonDestinationSlug.CHARLESTON:
      return CHARLESTON_DESTINATION;
    case HoneymoonDestinationSlug.BALI:
      return BALI_DESTINATION;
    case HoneymoonDestinationSlug.MALDIVES:
      return MALDIVES_DESTINATION;
    case HoneymoonDestinationSlug.EUROPE:
      return EUROPE_SUBDESTINATION;
    case HoneymoonDestinationSlug.OTHER:
      return OTHER_SUBDESTINATION;
    case HoneymoonDestinationSlug.CONTINENTAL_US:
      return CONTINENTAL_US_SUBDESTINATION;
    default:
      return EUROPE_SUBDESTINATION;
  }
};

export const getHoneymoonActiveSecondaryNav = (slug: HoneymoonDestinationSlug) => {
  switch (slug) {
    case HoneymoonDestinationSlug.HAWAII:
      return SecondaryNavIdentifier.DESTINATION_HAWAII;
    case HoneymoonDestinationSlug.CARIBBEAN:
      return SecondaryNavIdentifier.DESTINATION_CARIBBEAN;
    case HoneymoonDestinationSlug.ITALY:
      return SecondaryNavIdentifier.DESTINATION_EUROPE;
    case HoneymoonDestinationSlug.GREECE:
      return SecondaryNavIdentifier.DESTINATION_EUROPE;
    case HoneymoonDestinationSlug.SPAIN:
      return SecondaryNavIdentifier.DESTINATION_EUROPE;
    case HoneymoonDestinationSlug.FRANCE:
      return SecondaryNavIdentifier.DESTINATION_EUROPE;
    case HoneymoonDestinationSlug.MEXICO_CENTRAL_AMERICA:
      return SecondaryNavIdentifier.DESTINATION_MEXICO_CENTRAL_AMERICA;
    case HoneymoonDestinationSlug.FLORIDA_KEYS:
      return SecondaryNavIdentifier.DESTINATION_CONTINENTAL_US;
    case HoneymoonDestinationSlug.NAPA:
      return SecondaryNavIdentifier.DESTINATION_CONTINENTAL_US;
    case HoneymoonDestinationSlug.CALIFORNIA_COAST:
      return SecondaryNavIdentifier.DESTINATION_CONTINENTAL_US;
    case HoneymoonDestinationSlug.NATIONAL_PARKS:
      return SecondaryNavIdentifier.DESTINATION_CONTINENTAL_US;
    case HoneymoonDestinationSlug.CHARLESTON:
      return SecondaryNavIdentifier.DESTINATION_CONTINENTAL_US;
    case HoneymoonDestinationSlug.BALI:
      return SecondaryNavIdentifier.DESTINATION_OTHER;
    case HoneymoonDestinationSlug.MALDIVES:
      return SecondaryNavIdentifier.DESTINATION_OTHER;
    case HoneymoonDestinationSlug.EUROPE:
      return SecondaryNavIdentifier.DESTINATION_EUROPE;
    case HoneymoonDestinationSlug.OTHER:
      return SecondaryNavIdentifier.DESTINATION_OTHER;
    case HoneymoonDestinationSlug.CONTINENTAL_US:
      return SecondaryNavIdentifier.DESTINATION_CONTINENTAL_US;
    default:
      return '';
  }
};

const ProductDetails = styled.div`
  max-width: 100%;

  p {
    white-space: break-spaces;
  }
`;

export const ProductName = styled(P.BodyBase)`
  font-weight: ${FONT.FONT_WEIGHT_SEMI_BOLD};
`;

export const mapProductsToCategoryTile = (products: WProductView[]): CategoryTileProps[] => {
  return products.map(product => {
    const activeProductLook = product.product_look_views?.[0];
    return {
      linkElement: (
        <ProductDetails>
          <P.BodyBase color="BLACK_050">{product.brand?.name}</P.BodyBase>
          <ProductName>{product.display_name} </ProductName>
          <P.BodyBase color="BLACK_050">
            {getFormattedPrice(activeProductLook?.price_range)}
          </P.BodyBase>
        </ProductDetails>
      ),
      href: `/shop/product/${activeProductLook?.key}`,
      altText: '',
      image: activeProductLook?.images?.[0]?.aspect_ratios?.['1x1'].base,
    };
  });
};
